import * as React from "react"
import { languageName } from "../lib/docs"
import { apply, current, type Theme } from "../lib/theme"

/**
 * Who you are, and the few things that are yours to set.
 *
 * The sidebar used to end with an address, a tier and a sign-out link laid out flat. That
 * is three unrelated facts competing with the navigation above them, and it had nowhere to
 * put a fourth. This is the fourth's home.
 *
 * **The split inside is the honest part.** What this panel can change is what belongs to
 * *this screen* — the theme. What it can only report is what belongs to the *repository*:
 * which languages the documentation is kept in, which sections it has, which paths the
 * agent may write. Those are `.agent-languages`, `.agent-lanes` and `.agent-writable`, and
 * they are files in the repository on purpose (`ORG-3`): two people reading one repository
 * must not disagree about them, and a setting here would let them.
 *
 * Saying so is not a footnote. Somebody looking for "add a language" will look here first,
 * and the useful answer is where the switch actually is — not a switch that quietly
 * disagrees with the file.
 */
export const Profile: React.FC<{
  who: string
  repo: string
  languages: string[]
  lanes: Array<{ id: string; label: string; hidden: boolean }>
  onSignOut: () => void
}> = ({ who, repo, languages, lanes, onSignOut }) => {
  const [open, setOpen] = React.useState(false)
  const [theme, setTheme] = React.useState<Theme>(() => current())
  const [email, tier] = split(who)

  const choose = (t: Theme) => { apply(t); setTheme(t) }

  return (
    <div className={`sf-profile ${open ? "is-open" : ""}`}>
      {open && (
        <div className="sf-profile-panel" role="dialog" aria-label="Profile">
          <p className="sf-panel-head">Account</p>
          <p className="sf-panel-row"><b>{email}</b></p>
          {tier && <p className="sf-panel-row sf-quiet">Subscription: {tier}</p>}

          <p className="sf-panel-head">Settings</p>
          <div className="sf-seg" role="group" aria-label="Theme">
            {(["system", "light", "dark"] as Theme[]).map(t => (
              <button key={t} className={`sf-seg-opt ${theme === t ? "is-active" : ""}`}
                      onClick={() => choose(t)}>{t}</button>
            ))}
          </div>

          <p className="sf-panel-head">This repository decides</p>
          <p className="sf-panel-row sf-quiet">
            Set in the repository, not here — so two machines reading it cannot disagree.
          </p>
          <dl className="sf-facts">
            <dt>languages</dt>
            <dd>{languages.map(languageName).join(" · ") || "—"}<code>.agent-languages</code></dd>
            <dt>sections</dt>
            <dd>
              {lanes.map(l => l.label + (l.hidden ? " (hidden)" : "")).join(" · ") || "—"}
              <code>.agent-lanes</code>
            </dd>
            <dt>repository</dt>
            <dd>{repo || "—"}</dd>
          </dl>

          <div className="sf-side-actions">
            <button className="sf-ghost" onClick={onSignOut}>sign out</button>
          </div>
        </div>
      )}

      <button className="sf-profile-btn" aria-expanded={open}
              onClick={() => setOpen(v => !v)}>
        <span className="sf-avatar" aria-hidden="true">{(email[0] ?? "?").toUpperCase()}</span>
        <span className="sf-profile-text">
          <b>Profile</b>
          <span>{email}</span>
        </span>
        <span className="sf-twist" aria-hidden="true">{open ? "▾" : "▴"}</span>
      </button>
    </div>
  )
}

/** `ruslan@faion.net · geek` arrives as one string from the entitlement check. */
function split(who: string): [string, string] {
  const [email, ...rest] = who.split("·").map(s => s.trim())
  return [email ?? who, rest.join(" · ")]
}
